import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Wordmark } from "@/components/logo";

/** Split-panel auth shell: brand story on the left, form on the right.
 *  The right side intentionally echoes the iOS auth screen's calm, focused
 *  feel, tailored to a wider web canvas. */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <aside className="relative hidden overflow-hidden bg-brand-700 lg:flex lg:flex-col lg:justify-between">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand-500/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-accent-500/20 blur-3xl" />
        <div className="relative p-10">
          <Link to="/" className="text-white">
            <Wordmark className="text-xl text-white" />
          </Link>
        </div>
        <div className="relative max-w-md p-10 text-white">
          <p className="text-2xl font-semibold leading-snug">
            {t(
              "“Earn your screen time by learning — generate a set, practise, and watch the minutes add up.”",
            )}
          </p>
          <p className="mt-4 text-brand-200">
            {t("The EduPause learning loop, now in your browser.")}
          </p>
        </div>
      </aside>

      <main className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 lg:hidden">
            <Link to="/" className="text-ink">
              <Wordmark className="text-lg" />
            </Link>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          <p className="mt-1 text-sm text-ink-faint">{subtitle}</p>
          <div className="mt-6">{children}</div>
          <div className="mt-6 text-sm text-ink-faint">{footer}</div>
        </div>
      </main>
    </div>
  );
}
